import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Loader2, CreditCard, Lock } from 'lucide-react';
import { createStripeCheckout, createPaypalCheckout } from '../lib/api';
import logger from '../lib/logger';

const PayPalLogo = ({ size = 18 }) => (
  <svg width={size * 1.4} height={size} viewBox="0 0 124 33" xmlns="http://www.w3.org/2000/svg">
    <path fill="#253B80" d="M46.2 6.7h-6.5c-.4 0-.8.3-.9.8l-2.6 16.7c0 .3.2.6.5.6h3.1c.4 0 .8-.3.9-.8l.7-4.5c.1-.4.4-.8.9-.8h2c4.3 0 6.7-2.1 7.4-6.2.3-1.8 0-3.2-.8-4.2-1-1.1-2.7-1.6-4.7-1.6zm.7 6.1c-.4 2.6-2.3 2.6-4.2 2.6h-1.1l.7-4.7c0-.3.3-.5.6-.5h.5c1.3 0 2.5 0 3.2.7.4.5.5 1.1.3 1.9zM65.6 12.7h-3.1c-.3 0-.5.2-.6.5l-.1.9-.2-.3c-.7-1-2.2-1.3-3.7-1.3-3.5 0-6.5 2.7-7.1 6.4-.3 1.9.1 3.7 1.2 4.9.9 1.1 2.4 1.6 4 1.6 2.8 0 4.4-1.8 4.4-1.8l-.1.9c0 .3.2.6.5.6h2.8c.4 0 .8-.3.9-.8l1.7-10.9c.1-.3-.2-.7-.6-.7zm-4.3 6.2c-.3 1.8-1.8 3.1-3.6 3.1-1 0-1.7-.3-2.2-.9-.5-.6-.7-1.4-.5-2.3.3-1.8 1.8-3.1 3.6-3.1.9 0 1.6.3 2.2.9.5.6.7 1.4.5 2.3z"/>
    <path fill="#179BD7" d="M84.8 12.7h-3.1c-.3 0-.6.1-.7.4l-4.3 6.3-1.8-6c-.1-.4-.5-.6-.8-.6h-3c-.4 0-.6.4-.5.7l3.4 10-3.2 4.5c-.3.4 0 .8.4.8h3.1c.3 0 .6-.1.7-.4l10.3-14.9c.2-.4-.1-.8-.5-.8zM95.1 6.7h-6.5c-.4 0-.8.3-.9.8l-2.6 16.7c0 .3.2.6.5.6h3.3c.3 0 .6-.2.6-.5l.7-4.7c.1-.4.4-.8.9-.8h2c4.3 0 6.7-2.1 7.4-6.2.3-1.8 0-3.2-.8-4.2-1-1.1-2.6-1.7-4.6-1.7zm.7 6.1c-.4 2.6-2.3 2.6-4.2 2.6h-1.1l.7-4.7c0-.3.3-.5.6-.5h.5c1.3 0 2.5 0 3.2.7.4.5.5 1.1.3 1.9zM114.5 12.7h-3.1c-.3 0-.5.2-.6.5l-.1.9-.2-.3c-.7-1-2.2-1.3-3.7-1.3-3.5 0-6.5 2.7-7.1 6.4-.3 1.9.1 3.7 1.2 4.9.9 1.1 2.4 1.6 4 1.6 2.8 0 4.4-1.8 4.4-1.8l-.1.9c0 .3.2.6.5.6h2.8c.4 0 .8-.3.9-.8l1.7-10.9c.1-.3-.2-.7-.6-.7zm-4.3 6.2c-.3 1.8-1.8 3.1-3.6 3.1-1 0-1.7-.3-2.2-.9-.5-.6-.7-1.4-.5-2.3.3-1.8 1.8-3.1 3.6-3.1.9 0 1.6.3 2.2.9.5.6.7 1.4.5 2.3zM118.3 7.2l-2.7 17c0 .3.2.6.5.6h2.7c.4 0 .8-.3.9-.8l2.6-16.7c0-.3-.2-.6-.5-.6h-3c-.3-.1-.4.1-.5.5z"/>
  </svg>
);

const CheckoutPage = () => {
  const { items, subtotal } = useCart();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled') === '1';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [method, setMethod] = useState('stripe');
  const [form, setForm] = useState({
    email: '', firstName: '', lastName: '', address: '',
    city: '', zip: '', country: 'Ireland',
  });

  if (items.length === 0) {
    return (
      <div className="bg-black text-white min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="mb-4">Your cart is empty.</p>
          <Link to="/shop" className="text-[#909090] hover:underline">Continue shopping</Link>
        </div>
      </div>
    );
  }

  const discount = subtotal * 0.05;
  const shipping = 3.99;
  const total = subtotal - discount + shipping;
  const onChange = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        ...form,
        items: items.map((it) => ({ id: it.id, qty: it.qty })),
        origin_url: window.location.origin,
      };
      const res = method === 'stripe'
        ? await createStripeCheckout(payload)
        : await createPaypalCheckout(payload);
      if (res.url) {
        localStorage.setItem('hh_pending_order', res.orderId);
        window.location.href = res.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      logger.error(err);
      setError(err?.response?.data?.detail || 'Could not start checkout. Please try again.');
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-2.5 rounded text-sm focus:outline-none focus:border-[#909090]';

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-8">Checkout</h1>

        {cancelled && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm rounded">
            Payment was cancelled. Your cart is still here — try again whenever you're ready.
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr_400px] gap-10">
          <div className="space-y-8">
            <section>
              <h2 className="text-lg font-semibold mb-4">Contact</h2>
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => onChange('email', e.target.value)} className={inputCls} />
            </section>
            <section>
              <h2 className="text-lg font-semibold mb-4">Shipping address</h2>
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="First name" value={form.firstName} onChange={(e) => onChange('firstName', e.target.value)} className={inputCls} />
                <input required placeholder="Last name" value={form.lastName} onChange={(e) => onChange('lastName', e.target.value)} className={inputCls} />
                <input required placeholder="Address" value={form.address} onChange={(e) => onChange('address', e.target.value)} className={`${inputCls} col-span-2`} />
                <input required placeholder="City" value={form.city} onChange={(e) => onChange('city', e.target.value)} className={inputCls} />
                <input required placeholder="Eircode / Postcode" value={form.zip} onChange={(e) => onChange('zip', e.target.value)} className={inputCls} />
                <select value={form.country} onChange={(e) => onChange('country', e.target.value)} className={`${inputCls} col-span-2`}>
                  {['Ireland','United Kingdom','Netherlands','Germany','France','Spain','Italy','Belgium','Austria','Portugal','United States','Other'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </section>
            <section>
              <h2 className="text-lg font-semibold mb-4">Payment method</h2>
              <div className="space-y-3">
                <label className={`flex items-center gap-3 p-4 border rounded cursor-pointer transition-colors ${method === 'stripe' ? 'border-[#909090] bg-zinc-950' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <input type="radio" name="pm" value="stripe" checked={method === 'stripe'} onChange={() => setMethod('stripe')} className="accent-[#909090]" />
                  <CreditCard className="text-[#909090]" size={22}/>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">Pay with card</p>
                    <p className="text-zinc-500 text-xs">Powered by Stripe · Card, Apple Pay, Google Pay</p>
                  </div>
                  <Lock size={16} className="text-zinc-500"/>
                </label>
                <label className={`flex items-center gap-3 p-4 border rounded cursor-pointer transition-colors ${method === 'paypal' ? 'border-[#909090] bg-zinc-950' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <input type="radio" name="pm" value="paypal" checked={method === 'paypal'} onChange={() => setMethod('paypal')} className="accent-[#909090]" />
                  <PayPalLogo size={20}/>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">Pay with PayPal</p>
                    <p className="text-zinc-500 text-xs">You'll be redirected to PayPal to complete payment</p>
                  </div>
                </label>
              </div>
            </section>
          </div>

          <aside className="bg-zinc-950 border border-zinc-900 rounded p-6 h-fit">
            <h2 className="text-xl font-bold mb-5">Order summary</h2>
            <ul className="divide-y divide-zinc-900 mb-5">
              {items.map((it) => (
                <li key={it.id} className="py-3 flex gap-3 items-center">
                  <img src={it.image} alt={it.name} className="w-12 h-12 object-contain bg-zinc-900 rounded p-1" />
                  <div className="flex-1">
                    <p className="text-sm text-white">{it.name}</p>
                    <p className="text-xs text-zinc-500">Qty: {it.qty}</p>
                  </div>
                  <span className="text-sm text-[#909090]">€ {(it.price * it.qty).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="space-y-2 text-sm pb-4 border-b border-zinc-900">
              <div className="flex justify-between"><span className="text-zinc-400">Subtotal</span><span>€ {subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-green-400"><span>Discount (5%)</span><span>− € {discount.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Shipping</span><span>€ {shipping.toFixed(2)}</span></div>
            </div>
            <div className="flex justify-between items-baseline py-4 border-b border-zinc-900 mb-5">
              <span className="font-semibold">Total</span>
              <span className="text-2xl font-bold text-[#909090]">€ {total.toFixed(2)}</span>
            </div>
            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            <button type="submit" disabled={submitting} className="w-full bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold py-3.5 rounded transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? <><Loader2 className="animate-spin" size={18}/> Redirecting...</> : <><Lock size={16}/> Pay € {total.toFixed(2)}</>}
            </button>
            <p className="text-xs text-zinc-500 mt-3 text-center">Secure checkout</p>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
